import App from '../App';
import { useSearchParams } from 'react-router-dom';

import addSlotGif from './partners/assets/add_slot.gif';

const styles = `
.partners-page {
  --bim-blue: #7AB8E8;
  --bim-blue-dark: #4F9EDB;
  --bim-amber: #E8A33D;
  --bim-amber-dark: #C9822A;
  --bim-amber-soft: #FFF6E9;
  --text: #111111;
  --text-muted: #6B7280;
  --line: #E5E7EB;
  --bg: #FFFFFF;
  --bg-soft: #F7FAFC;
  --radius: 14px;
  --shadow: 0 1px 2px rgba(17, 17, 17, 0.04), 0 4px 16px rgba(17, 17, 17, 0.04);

  box-sizing: border-box;
  margin: 0;
  padding: 0;
  background: var(--bg);
  color: var(--text);
  font-family: -apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  font-size: 16px;
  line-height: 1.55;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.partners-page *, .partners-page *::before, .partners-page *::after { box-sizing: border-box; }

.partners-page a { color: var(--bim-blue-dark); text-decoration: none; }
.partners-page a:hover { text-decoration: underline; }

.partners-page .container {
  max-width: 880px;
  margin: 0 auto;
  padding: 32px 24px 96px;
}

/* Hero */
.partners-page .greeting {
  font-size: 40px;
  line-height: 1.15;
  letter-spacing: -0.01em;
  font-weight: 700;
  color: var(--text);
  margin: 0 0 8px;
}

.partners-page .eyebrow {
  display: inline-block;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--bim-blue-dark);
  background: var(--bg-soft);
  padding: 6px 12px;
  border-radius: 999px;
  margin: 0 0 16px;
}

.partners-page .hero p {
  font-size: 18px;
  color: var(--text-muted);
  margin: 0 0 12px;
}

.partners-page .hero p strong {
  color: var(--text);
  font-weight: 600;
}

.partners-page .brand {
  color: #76C6FF;
  font-weight: 600;
}

.partners-page .hero .note {
  font-size: 14px;
  color: var(--text-muted);
  margin-top: 20px;
}

/* Benefits grid */
.partners-page .benefits {
  margin-top: 56px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.partners-page .benefit-card {
  padding: 24px;
  background: var(--bg-soft);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  transition: box-shadow 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
}

.partners-page .benefit-card:hover {
  box-shadow: var(--shadow);
  border-color: var(--bim-blue);
  transform: translateY(-2px);
}

.partners-page .benefit-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border-radius: 10px;
  color: var(--bim-blue-dark);
  box-shadow: var(--shadow);
  margin-bottom: 16px;
}

.partners-page .benefit-tag {
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: var(--bim-blue-dark);
  margin-bottom: 8px;
}

.partners-page .benefit-name {
  font-size: 17px;
  font-weight: 600;
  color: var(--text);
  margin-bottom: 6px;
}

.partners-page .benefit-meta {
  font-size: 14px;
  color: var(--text-muted);
  line-height: 1.5;
}

/* Sections */
.partners-page section.block { margin-top: 64px; }
.partners-page section.block h2 { font-size: 24px; font-weight: 700; margin: 0 0 12px; }
.partners-page section.block p { color: var(--text-muted); margin: 0 0 12px; font-size: 15px; line-height: 1.6; }
.partners-page section.block ul { padding-left: 20px; margin: 8px 0 0; color: var(--text-muted); font-size: 15px; line-height: 1.7; }

/* Demo GIF */
.partners-page .demo { margin: 32px 0 0; text-align: center; }
.partners-page .demo img {
  width: 260px;
  max-width: 72%;
  border-radius: 20px;
  border: 1px solid var(--line);
  box-shadow: var(--shadow);
  display: inline-block;
  vertical-align: top;
}
.partners-page .demo figcaption {
  margin-top: 14px;
  font-size: 14px;
  color: var(--text-muted);
  max-width: 460px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.5;
}

.partners-page .how-grid {
  display: grid;
  grid-template-columns: 1fr 260px;
  gap: 40px;
  align-items: start;
  margin-top: 24px;
}
.partners-page .how-grid .steps { margin-top: 0; }
.partners-page .how-grid .demo { margin: 0; }
.partners-page .how-grid .demo img { width: 100%; max-width: 260px; }

/* Steps */
.partners-page .steps { margin-top: 20px; display: grid; gap: 20px; }

.partners-page .step { display: flex; gap: 18px; align-items: flex-start; }

.partners-page .step-num {
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: var(--bim-blue);
  color: #fff;
  font-weight: 700;
  font-size: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.partners-page .step-body { padding-top: 4px; }
.partners-page .step-name { font-size: 17px; font-weight: 600; color: var(--text); margin-bottom: 4px; }
.partners-page .step-meta { font-size: 15px; color: var(--text-muted); line-height: 1.55; }

/* Bonus highlight card */
.partners-page .bonus {
  margin-top: 64px;
  padding: 28px 32px;
  background: var(--bim-amber-soft);
  border: 1px solid #F3DDB2;
  border-radius: var(--radius);
}

.partners-page .bonus h3 {
  margin: 0 0 10px;
  font-size: 20px;
  font-weight: 700;
  color: #7A5417;
}

.partners-page .bonus p { margin: 0 0 10px; font-size: 15px; line-height: 1.6; color: #7A5417; }
.partners-page .bonus p:last-child { margin-bottom: 0; }
.partners-page .bonus strong { color: #5C4012; }

/* CTA card */
.partners-page .cta {
  margin-top: 64px;
  padding: 32px;
  background: var(--bg-soft);
  border-radius: var(--radius);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
}

.partners-page .cta-text { flex: 1 1 320px; }
.partners-page .cta h3 { margin: 0 0 4px; font-size: 20px; font-weight: 700; }
.partners-page .cta p { margin: 0; color: var(--text-muted); font-size: 15px; line-height: 1.55; }

.partners-page .cta-alt { margin-top: 12px !important; font-size: 13px !important; }
.partners-page .cta-alt a { white-space: nowrap; }

.partners-page .cta-reply { flex: 0 1 280px; }

.partners-page .bubble {
  background: var(--bim-blue);
  color: #fff;
  font-size: 15px;
  line-height: 1.45;
  padding: 12px 16px;
  border-radius: 18px 18px 4px 18px;
}

.partners-page .bubble-hint {
  margin-top: 8px;
  font-size: 13px;
  color: var(--text-muted);
  text-align: right;
}

/* Footer */
.partners-page .footer {
  margin-top: 80px;
  padding-top: 24px;
  border-top: 1px solid var(--line);
  color: var(--text-muted);
  font-size: 13px;
  line-height: 1.6;
}

.partners-page .footer .row { display: flex; justify-content: space-between; flex-wrap: wrap; gap: 16px; }
.partners-page .footer a { color: var(--text-muted); }
.partners-page .footer a:hover { color: var(--text); }

@media (max-width: 640px) {
  .partners-page .hero h1 { font-size: 30px; }
  .partners-page .hero p { font-size: 16px; }
  .partners-page .benefits { grid-template-columns: 1fr; }
  .partners-page .how-grid { grid-template-columns: 1fr; gap: 28px; }
  .partners-page .cta { padding: 24px; }
  .partners-page .bonus { padding: 22px; }
}
`;

/*
  Направления (необязательная «донастройка»). Без параметра d страница универсальная.
  Ссылка: /partners?name=Имя&d=ключ
  Новое направление = новый ключ в объекте ниже. Не заданные поля остаются как в разметке.
  lead       — первый абзац после приветствия (**текст** выделяется жирным)
  request    — как клиент выбирает услугу (лид «Как это устроено»)
  experience — первый пункт «Кого мы ищем» (без точки с запятой)
*/
const directions = {
  massage: {
    lead: "Сейчас активно развиваем направление **кастомизируемого массажа** и ищем партнёра — мастера своего дела.",
    request: "Клиент собирает массаж под свой запрос",
    experience: "опыт в оздоровительном (релаксационном) массаже — ваше образование и практика особенно ценны",
  },
  nails: {
    lead: "Сейчас активно развиваем направление **ногтевого сервиса** и ищем партнёра — мастера своего дела.",
    request: "Клиент выбирает услугу под свой запрос",
    experience: "опыт в ногтевом сервисе",
  },
};

const defaults = {
  lead: "Сейчас активно развиваем услуги эстетической и оздоровительной направленности: **массаж**, **ногтевой сервис** и другие направления. Ищем партнёров — мастеров своего дела.",
  request: "Клиент выбирает услугу под свой запрос",
  experience: "опыт работы по вашему направлению — ваше образование и практика особенно ценны",
};

function renderMd(text) {
  return text.split('**').map((part, i) => (i % 2 ? <strong>{part}</strong> : part));
}

export default function PartnersPage() {
  const [searchParams] = useSearchParams();

  const raw = (searchParams.get('name') || '').trim().slice(0, 40);
  const name = raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : null;

  const key = (searchParams.get('d') || '').trim().toLowerCase();
  const profile = Object.prototype.hasOwnProperty.call(directions, key) ? directions[key] : null;
  const text = (k) => (profile && typeof profile[k] === 'string' && profile[k] ? profile[k] : defaults[k]);

  return (
    <App>
      <style>{styles}</style>
      <div className="partners-page">
        <div className="container">

          <section className="hero">
            <span className="eyebrow">Партнёрам</span>
            <h1 className="greeting">{name ? `Здравствуйте, ${name} 🙂` : 'Здравствуйте 🙂'}</h1>
            <p>
              В <span className="brand">bim</span> мы строим медицинскую компанию с особой
              культурой — максимально ответственного и доброжелательного отношения
              к клиентам, сотрудникам и партнёрам.
            </p>
            <p data-d="lead">{renderMd(text('lead'))}</p>
            <p>
              <span className="brand">bim</span> — молодая компания, и каждый партнёр
              для нас — событие, а не строчка в штатном расписании. Предлагаем
              частичную занятость и подработку с гибким графиком в центре Москвы.
              Мы искренне ценим наших партнёров, поэтому хотим сделать сотрудничество
              комфортным и выгодным для вас:
            </p>

          </section>

          <div className="benefits" aria-label="Условия сотрудничества">

            <div className="benefit-card">
              <div className="benefit-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="2.5"/><path d="M6 12h.01M18 12h.01"/></svg>
              </div>
              <div className="benefit-tag">выше рынка</div>
              <div className="benefit-name">Оплата за услугу</div>
              <div className="benefit-meta">Платим за каждую выполненную услугу — переводом на ваш счёт</div>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3.5 2"/></svg>
              </div>
              <div className="benefit-tag">вы выбираете</div>
              <div className="benefit-name">Гибкий график</div>
              <div className="benefit-meta">Сами открываете удобные слоты в приложении и берёте только те заказы, которые вам подходят</div>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21s-7-6.1-7-11a7 7 0 0 1 14 0c0 4.9-7 11-7 11Z"/><circle cx="12" cy="10" r="2.5"/></svg>
              </div>
              <div className="benefit-tag">центр Москвы</div>
              <div className="benefit-name">Локация</div>
              <div className="benefit-meta">Кутузовский&nbsp;проспект,&nbsp;22 и 1-я&nbsp;Тверская-Ямская,&nbsp;28 — оборудованные студии, без разъездов по городу</div>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l2.4 5 5.6.8-4 3.9.9 5.5L12 21.5 7.1 18.2 8 12.7 4 8.8 9.6 8z"/></svg>
              </div>
              <div className="benefit-tag">до 1&nbsp;800&nbsp;₽ в неделю</div>
              <div className="benefit-name">Бонус за открытое расписание</div>
              <div className="benefit-meta">Три окна по 2 часа в неделю — и доплата до 1&nbsp;800&nbsp;₽ сверх оплаты за заказы (нужен хотя бы один выполненный заказ)</div>
            </div>

          </div>


          <section className="block">
            <h2>Как это устроено</h2>
            <p>
              От вас — только сами услуги.
              {' '}<span data-d="request">{text('request')}</span>
              {' '}в приложении, а поиск, чаты и согласование цены остаются на нашей стороне.
            </p>
            <div className="how-grid">
              <div className="steps">

                <div className="step">
                  <div className="step-num">1</div>
                  <div className="step-body">
                    <div className="step-name">Открываете слоты</div>
                    <div className="step-meta">Отмечаете в приложении время, когда вам удобно работать.</div>
                  </div>
                </div>

                <div className="step">
                  <div className="step-num">2</div>
                  <div className="step-body">
                    <div className="step-name">Мы находим клиента</div>
                    <div className="step-meta">Общение, согласование деталей и т. д. берём на себя.</div>
                  </div>
                </div>

                <div className="step">
                  <div className="step-num">3</div>
                  <div className="step-body">
                    <div className="step-name">Получаете готовый заказ</div>
                    <div className="step-meta">Запись приходит на ваш слот со всеми деталями заказа.</div>
                  </div>
                </div>

                <div className="step">
                  <div className="step-num">4</div>
                  <div className="step-body">
                    <div className="step-name">Оказываете услугу — и получаете оплату</div>
                    <div className="step-meta">Оплата выше рынка — переводом с расчётного счёта на ваш счёт.</div>
                  </div>
                </div>

              </div>

              <figure className="demo">
                <img src={addSlotGif} alt="Добавление свободного слота в приложении bim: выбор даты, времени и подтверждение" />
                <figcaption>Шаг 1 в приложении: выбираете дату, время и подтверждаете.</figcaption>
              </figure>
            </div>
          </section>

          <section className="block">
            <h2>Кого мы ищем</h2>
            <ul>
              <li><span data-d="experience">{text('experience')}</span>;</li>
              <li>документ о квалификации — сертификат или диплом о профподготовке;</li>
              <li>статус самозанятого или ИП. Если статуса пока нет — первые заказы закроем наличными или переводом, пока оформляетесь.</li>
            </ul>
            <p>
              Сотрудничество оформляется по договору оказания услуг.
              Вы самостоятельно принимаете или отклоняете предложенные заказы.
              Подробнее можно ознакомиться в <a href="https://bimclinics.ru/offer-specialist">оферте</a>.
            </p>
          </section>

          <div className="cta">
            <div className="cta-text">
              <h3>Интересно? Просто ответьте на сообщение</h3>
              <p>
                Эту страницу мы прислали вам лично — ответьте там же, где получили
                ссылку, и продолжим разговор: ответим на вопросы, покажем
                приложение и студию.
              </p>
              <p className="cta-alt">
                Удобнее голосом? <a href="tel:+79932731083">+7&nbsp;(993)&nbsp;273-10-83</a>
                {' '}· <a href="mailto:hello@bimclinics.ru">hello@bimclinics.ru</a>
              </p>
            </div>
            <div className="cta-reply">
              <div className="bubble">Здравствуйте! Да, интересно — расскажите подробнее</div>
              <div className="bubble-hint">— например, вот так 🙂</div>
            </div>
          </div>

        </div>
      </div>
    </App>
  );
}
